import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useProfileProgressionView from "./profile-progression/useProfileProgressionView";
import ProfileProgressionSummary from "./profile-progression/ProfileProgressionSummary";
import ProfileProgressionStats from "./profile-progression/ProfileProgressionStats";
import ProfileProgressionDetails from "./profile-progression/ProfileProgressionDetails";
import { trackProductEvent } from "../analytics/trackEvent";
import "../styles/components/ProfileProgressionCard.css";

const PROFILE_COMPLETION_PROMPT_VERSION = "v1";

function getProfileCompletionPromptKey(userId) {
  return `profileCompletionPromptDismissed:${PROFILE_COMPLETION_PROMPT_VERSION}:${userId}`;
}

function ProfileCompletionPrompt({ userId, onEditBio }) {
  const { t } = useTranslation();
  const storageKey = useMemo(
    () => (userId ? getProfileCompletionPromptKey(userId) : ""),
    [userId]
  );
  const [isDismissed, setIsDismissed] = useState(true);

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") {
      setIsDismissed(true);
      return;
    }

    try {
      setIsDismissed(window.localStorage.getItem(storageKey) === "1");
    } catch {
      setIsDismissed(false);
    }
  }, [storageKey]);

  const handleDismiss = () => {
    setIsDismissed(true);
    trackProductEvent("profile_completion_prompt_dismissed", {
      featureArea: "profile",
      metadata: { source: "profile_completion_prompt" },
    });

    if (!storageKey || typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(storageKey, "1");
    } catch {
      // Non-fatal: storage can be unavailable in private or restricted contexts.
    }
  };

  const handleEditBioClick = () => {
    trackProductEvent("profile_completion_prompt_cta_clicked", {
      featureArea: "profile",
      metadata: { source: "profile_completion_prompt" },
    });
    if (typeof onEditBio === "function") {
      onEditBio();
    }
  };

  if (!storageKey || isDismissed) {
    return null;
  }

  return (
    <section
      className="profile-completion-prompt"
      aria-labelledby="profile-completion-prompt-title"
    >
      <button
        type="button"
        className="profile-completion-prompt__close"
        onClick={handleDismiss}
        aria-label={t("profile_completion_prompt_close_label", {
          defaultValue: "Close profile completion prompt",
        })}
      >
        {"\u00D7"}
      </button>
      <div className="profile-completion-prompt__content">
        <h3 id="profile-completion-prompt-title">
          {t("profile_completion_prompt_title", {
            defaultValue: "Complete your profile",
          })}
        </h3>
        <p>
          {t("profile_completion_prompt_body", {
            defaultValue:
              "You can add a short bio, fishing style, water type, favorite species, fishing methods, and social links from Edit bio. These details are optional and help other anglers get to know you.",
          })}
        </p>
      </div>
      <div className="profile-completion-prompt__actions">
        <button
          type="button"
          className="profile-completion-prompt__action profile-completion-prompt__action--primary"
          onClick={handleEditBioClick}
        >
          {t("profile_completion_prompt_edit_bio", {
            defaultValue: "Edit bio",
          })}
        </button>
        <button
          type="button"
          className="profile-completion-prompt__action profile-completion-prompt__action--secondary"
          onClick={handleDismiss}
        >
          {t("profile_completion_prompt_dismiss", {
            defaultValue: "Do not show again",
          })}
        </button>
      </div>
    </section>
  );
}

export default function ProfileProgressionCard({
  progression,
  profileDetails = null,
  directoryUsers = null,
  className = "",
  isOwnProfile = false,
  bioEditRequest = null,
  isBioEditRequestReady = false,
  onBioEditRequestConsumed,
}) {
  const progressionView = useProfileProgressionView({
    progression,
    profileDetails,
    directoryUsers,
    isOwnProfile,
    bioEditRequest,
    isBioEditRequestReady,
    onBioEditRequestConsumed,
  });
  const profileUserId = profileDetails?.id || "";
  const shouldShowCompletionPrompt = Boolean(isOwnProfile && profileUserId);

  if (!progression && !shouldShowCompletionPrompt) return null;

  return (
    <>
      <section className={`profile-progression-card ${className}`.trim()}>
        {progression ? (
          <>
            <ProfileProgressionSummary progressionView={progressionView} />
            <ProfileProgressionStats progressionView={progressionView} />
          </>
        ) : null}
        {shouldShowCompletionPrompt ? (
          <ProfileCompletionPrompt
            userId={profileUserId}
            onEditBio={progressionView.handleOpenBioEditor}
          />
        ) : null}
      </section>
      <ProfileProgressionDetails progressionView={progressionView} />
    </>
  );
}
